import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  Image,
  FileText,
  CheckCircle,
  AlertCircle,
  X,
  Camera,
  Loader2,
  Eye,
  Download
} from 'lucide-react';
import { uploadAPI } from '../services/api';
import toast from 'react-hot-toast';

const BusinessCardUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processingFiles, setProcessingFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const queryClient = useQueryClient();

  const uploadMutation = useMutation(uploadAPI.uploadBusinessCard, {
    onSuccess: (result) => {
      toast.success('Business card analyzed successfully!');
      if (result.data) {
        setResults(prev => [...prev, result.data]);
      }
      // Dosya durumunu güncelle
      setProcessingFiles(prev => prev.map(file => ({
        ...file,
        status: 'completed'
      })));
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries('dashboardStats');
    },
    onError: (error) => {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || 'An error occurred while processing the business card';
      toast.error(errorMessage);
      setErrors(prev => [...prev, { message: errorMessage }]);
      // Dosya durumunu hata olarak güncelle
      setProcessingFiles(prev => prev.map(file => ({
        ...file,
        status: 'error'
      })));
    }
  });

  const bulkUploadMutation = useMutation(uploadAPI.uploadBusinessCardsBulk, {
    onSuccess: (result) => {
      if (result.data && result.data.summary) {
        toast.success(`${result.data.summary.successful} business cards processed successfully!`);
        if (result.data.results) {
          setResults(prev => [...prev, ...result.data.results]);
        }
        if (result.data.errors) {
          setErrors(prev => [...prev, ...result.data.errors]);
        }
        // Dosya durumunu güncelle
        setProcessingFiles(prev => prev.map(file => ({
          ...file,
          status: 'completed'
        })));
        queryClient.invalidateQueries(['leads']);
        queryClient.invalidateQueries('dashboardStats');
      } else {
        toast.success('Business cards processed successfully!');
        // Dosya durumunu güncelle
        setProcessingFiles(prev => prev.map(file => ({
          ...file,
          status: 'completed'
        })));
      }
    },
    onError: (error) => {
      console.error('Bulk upload error:', error);
      const errorMessage = error.response?.data?.error || 'An error occurred during bulk upload';
      toast.error(errorMessage);
      // Dosya durumunu hata olarak güncelle
      setProcessingFiles(prev => prev.map(file => ({
        ...file,
        status: 'error'
      })));
    }
  });

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file),
      status: 'pending'
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
    setProcessingFiles(prev => [...prev, ...newFiles]);

    // Tek dosya yükleme
    if (acceptedFiles.length === 1) {
      const file = acceptedFiles[0];
      uploadMutation.mutate(file);
    } else {
      // Toplu yükleme
      bulkUploadMutation.mutate(acceptedFiles);
    }
  }, [uploadMutation, bulkUploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: true
  });

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setProcessingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const clearAll = () => {
    setUploadedFiles([]);
    setProcessingFiles([]);
    setResults([]);
    setErrors([]);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Upload Business Card</h1>
          <p className="mt-2 text-gray-600">
            Upload business card photos, AI will automatically extract the information
          </p>
        </div>
        {uploadedFiles.length > 0 && (
          <button
            onClick={clearAll}
            className="btn-secondary"
          >
            <X size={16} className="mr-2" />
            Clear
          </button>
        )}
      </div>

      {/* Upload Area */}
      <div className="card mb-6">
        <div className="card-body">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${isDragActive
              ? 'border-primary-400 bg-primary-50'
              : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
              }`}
          >
            <input {...getInputProps()} />
            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                {isDragActive ? (
                  <Upload size={32} className="text-primary-600" />
                ) : (
                  <Camera size={32} className="text-primary-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {isDragActive ? 'Drop files here' : 'Upload business card photo'}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Drag and drop PNG, JPG, GIF files or click to select
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Maximum file size: 5MB
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Files */}
      <AnimatePresence>
        {processingFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Processing Files</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {processingFiles.map((file) => (
                  <motion.div
                    key={file.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="relative border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                      <img
                        src={file.preview}
                        alt={file.file.name}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          onClick={() => removeFile(file.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center">
                        {file.status === 'pending' && (
                          <>
                            <Loader2 size={16} className="animate-spin text-primary-600 mr-2" />
                            <span className="text-xs text-gray-600">Processing...</span>
                          </>
                        )}
                        {file.status === 'completed' && (
                          <>
                            <CheckCircle size={16} className="text-success-600 mr-2" />
                            <span className="text-xs text-success-600">Completed</span>
                          </>
                        )}
                        {file.status === 'error' && (
                          <>
                            <AlertCircle size={16} className="text-danger-600 mr-2" />
                            <span className="text-xs text-danger-600">Error</span>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results */}
      <AnimatePresence>
        {results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Successful Results</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {results.map((result, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-success-200 rounded-lg p-4 bg-success-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <CheckCircle size={20} className="text-success-600 mr-3" />
                        <div>
                          <h4 className="text-sm font-medium text-success-900">
                            Business card analyzed successfully
                          </h4>
                          <p className="text-sm text-success-700 mt-1">
                            {result.lead?.firstName} {result.lead?.lastName} - {result.lead?.company}
                          </p>
                          {result.ocrProvider && result.ocrProvider !== 'none' && (
                            <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Analyzed with: {result.ocrProvider === 'mistral' ? 'Mistral AI' :
                                result.ocrProvider === 'openai' ? 'OpenAI GPT-4o' :
                                  result.ocrProvider === 'google' ? 'Google Cloud Vision' :
                                    result.ocrProvider}
                            </div>
                          )}
                        </div>
                      </div>
                      <a
                        href={`/leads/${result.lead?.id || result.lead?._id}`}
                        className="text-sm text-success-600 hover:text-success-700 font-medium"
                        onClick={() => {
                        }}
                      >
                        View
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Errors */}
      <AnimatePresence>
        {errors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Errors</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {errors.map((error, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-danger-200 rounded-lg p-4 bg-danger-50"
                  >
                    <div className="flex items-start">
                      <AlertCircle size={20} className="text-danger-600 mr-3 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-danger-900">
                          Processing failed
                        </h4>
                        <p className="text-sm text-danger-700 mt-1">
                          {error.error || error.message || error.filename}
                        </p>
                        {error.details && (
                          <p className="text-xs text-danger-600 mt-1">
                            Details: {error.details}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tips */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Tips</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <Image size={16} className="text-primary-600" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Quality Photo</h4>
                <p className="text-sm text-gray-600">
                  Take the business card in a well-lit environment with clear focus
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <FileText size={16} className="text-primary-600" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Text Readability</h4>
                <p className="text-sm text-gray-600">
                  Ensure all text is clear and readable
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <CheckCircle size={16} className="text-primary-600" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Automatic Processing</h4>
                <p className="text-sm text-gray-600">
                  AI automatically extracts all information and saves to database
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <Eye size={16} className="text-primary-600" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Check Results</h4>
                <p className="text-sm text-gray-600">
                  Review extracted information and edit if necessary
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardUpload; 